/**
 * Report month-picker helpers (FE-only). The report query accepts
 * `date_from` / `date_to` (RFC3339 date-time). The month picker offers the
 * current month plus the previous several, computing the first → last instant
 * of the chosen month; "All time" clears the range.
 */

export interface MonthOption {
  /** Stable value used by the <option> and to look the range back up: "YYYY-MM". */
  value: string;
  /** Display label, e.g. "May 2026". */
  label: string;
}

/** Local-time first instant of the month (00:00:00.000). */
function monthStart(year: number, month: number): Date {
  return new Date(year, month, 1, 0, 0, 0, 0);
}

/** Local-time last instant of the month (last day 23:59:59.999). */
function monthEnd(year: number, month: number): Date {
  return new Date(year, month + 1, 0, 23, 59, 59, 999);
}

/**
 * Recent months, newest first: the month containing `now` and the prior
 * `count - 1` months.
 */
export function recentMonths(now: Date = new Date(), count = 6): MonthOption[] {
  const opts: MonthOption[] = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    opts.push({
      value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
      label: d.toLocaleString("en-US", { month: "short", year: "numeric" }),
    });
  }
  return opts;
}

export interface MonthRange {
  date_from: string;
  date_to: string;
}

/**
 * Resolve a "YYYY-MM" value to its [first, last] RFC3339 instants. Returns null
 * for an unparseable value (treated as "All time" by the caller).
 */
export function monthRange(value: string): MonthRange | null {
  const m = /^(\d{4})-(\d{2})$/.exec(value);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]) - 1;
  if (month < 0 || month > 11) return null;
  return {
    date_from: monthStart(year, month).toISOString(),
    date_to: monthEnd(year, month).toISOString(),
  };
}
